import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CheckCircle2,
  CornerDownRight,
  GitMerge,
  MessageSquare,
  Pencil,
  Send,
  ShieldAlert,
  ThumbsDown,
  Undo2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { normalizeContent, type ChangeRequestDetailDto, type CommentDto, type DocumentContent, type DocumentDiff, type ReviewDecision } from '@imsenta/shared';
import { PageHeader } from '@/components/AppShell';
import { DiffSummary, SectionDiffList } from '@/components/DiffView';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  StatusBadge,
  Tabs,
  Textarea,
  useToast,
} from '@/components/ui';
import { DocumentEditor, DocumentReadView } from '@/editor/DocumentEditor';
import { api, ApiError } from '@/lib/api';
import { fmtDateTime, fmtRelative } from '@/lib/format';
import { useSession } from '@/lib/session';

export function ChangeRequestPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const { session, can } = useSession();
  const [tab, setTab] = useState<'discussion' | 'changes' | 'proposal'>('changes');
  const [mergeOpen, setMergeOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftContent, setDraftContent] = useState<DocumentContent | null>(null);

  const { data: cr, isLoading, error } = useQuery({
    queryKey: ['change-request', id],
    queryFn: () => api.get<ChangeRequestDetailDto>(`/change-requests/${id}`),
  });

  const { data: diff } = useQuery({
    queryKey: ['change-request-diff', id],
    queryFn: () => api.get<DocumentDiff>(`/change-requests/${id}/diff`),
    enabled: Boolean(cr?.proposedVersion),
    retry: false,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['change-request', id] });
    queryClient.invalidateQueries({ queryKey: ['change-requests'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const review = useMutation({
    mutationFn: (input: { decision: ReviewDecision; comment?: string }) => api.post(`/change-requests/${id}/review`, input),
    onSuccess: () => {
      invalidate();
      push('Your review has been recorded', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not record your review', 'danger'),
  });

  const saveProposal = useMutation({
    mutationFn: (content: DocumentContent) => api.patch(`/change-requests/${id}`, { content }),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['change-request-diff', id] });
      setEditing(false);
      setDraftContent(null);
      push('Proposal updated', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not save the proposal', 'danger'),
  });

  const transition = useMutation({
    mutationFn: (state: string) => api.post(`/change-requests/${id}/transition`, { state }),
    onSuccess: () => {
      invalidate();
      setRejectOpen(false);
      push('Change request updated', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not update', 'danger'),
  });

  if (isLoading) return <Spinner label="Loading change request…" />;
  if (error || !cr) {
    return (
      <Card>
        <EmptyState
          title="Change request not available"
          description={error instanceof ApiError ? error.message : 'It may have been withdrawn.'}
          action={<Button onClick={() => navigate('/change-requests')}>Back to change requests</Button>}
        />
      </Card>
    );
  }

  const isAuthor = cr.createdBy?.id === session?.user.id;
  const isOpen = ['open', 'approved', 'changes_requested'].includes(cr.state);
  // The author keeps control of their proposal until it is decided; an
  // administrator can also correct one on their behalf.
  const canRevise = (isAuthor || can('changeRequest:decide')) && (isOpen || cr.state === 'draft');
  const myReview = cr.reviews.filter((r) => r.reviewer?.id === session?.user.id && r.current).at(-1);

  return (
    <>
      <PageHeader
        back={{ to: '/change-requests', label: 'Change requests' }}
        title={
          <span className="flex items-baseline gap-2">
            <span className="text-ink-400">#{cr.number}</span>
            {cr.title}
          </span>
        }
        meta={
          <>
            <StatusBadge status={cr.state} />
            <Link to={`/documents/${cr.documentId}`} className="text-[12.5px] text-brand-700 hover:underline">
              {cr.documentReference} · {cr.documentTitle}
            </Link>
            <span className="text-[12.5px] text-ink-500">
              Raised by {cr.createdBy?.name ?? 'Unknown'} {fmtRelative(cr.createdAt)}
            </span>
          </>
        }
        actions={
          <>
            {cr.state === 'draft' && (isAuthor || can('changeRequest:decide')) ? (
              <Button variant="primary" icon={<Send className="size-4" />} onClick={() => transition.mutate('open')}>
                Open for review
              </Button>
            ) : null}
            {isOpen && (isAuthor || can('changeRequest:decide')) ? (
              <Button icon={<Undo2 className="size-4" />} onClick={() => transition.mutate('withdrawn')}>
                Withdraw
              </Button>
            ) : null}
            {isOpen && can('changeRequest:decide') ? (
              <Button variant="danger" icon={<ThumbsDown className="size-4" />} onClick={() => setRejectOpen(true)}>
                Decline
              </Button>
            ) : null}
            {isOpen && can('changeRequest:merge') ? (
              <Button variant="success" icon={<GitMerge className="size-4" />} disabled={!cr.canMerge} onClick={() => setMergeOpen(true)}>
                Accept and merge
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          {cr.state === 'changes_requested' && canRevise ? (
            <div className="mb-4">
              <Alert tone="warning" title="Changes have been requested">
                Open the{' '}
                <button type="button" onClick={() => setTab('proposal')} className="font-medium underline">
                  Full proposal
                </button>{' '}
                tab to revise it. Saving a revision reopens the request for review.
              </Alert>
            </div>
          ) : null}

          {cr.description ? (
            <Card className="mb-4 px-5 py-4">
              <p className="mb-1.5 text-[11px] font-semibold tracking-wider text-ink-400 uppercase">Rationale</p>
              <p className="text-[14px] leading-relaxed whitespace-pre-wrap text-ink-700">{cr.description}</p>
            </Card>
          ) : null}

          <Tabs
            className="mb-4"
            value={tab}
            onChange={setTab}
            tabs={[
              { id: 'changes' as const, label: 'Proposed changes' },
              { id: 'proposal' as const, label: 'Full proposal' },
              { id: 'discussion' as const, label: 'Discussion', count: cr.comments.length },
            ]}
          />

          {tab === 'changes' ? (
            <Card className="px-5 py-4">
              {!diff ? (
                <Spinner />
              ) : (
                <>
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <DiffSummary diff={diff} />
                    <span className="text-[12px] text-ink-400">
                      {cr.baseVersion ? `Against ${cr.baseVersion.label}` : 'Against an empty document'}
                    </span>
                  </div>
                  <SectionDiffList diff={diff} />
                </>
              )}
            </Card>
          ) : null}

          {tab === 'proposal' ? (
            <Card className="px-5 py-6 sm:px-8 sm:py-7">
              {!cr.proposedVersion ? (
                <EmptyState title="No proposal content" />
              ) : editing && draftContent ? (
                <>
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-ink-200 pb-3">
                    <p className="text-[13px] text-ink-500">
                      Editing the proposal. Saving records a new revision, which resets any approvals already given.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setEditing(false);
                          setDraftContent(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button variant="primary" loading={saveProposal.isPending} onClick={() => saveProposal.mutate(draftContent)}>
                        Save proposal
                      </Button>
                    </div>
                  </div>
                  <DocumentEditor content={draftContent} documentId={cr.documentId} onChange={setDraftContent} />
                </>
              ) : (
                <>
                  <div className="mb-6 flex flex-wrap items-start justify-between gap-3 border-b border-ink-200 pb-4">
                    <h1 className="text-2xl font-semibold text-ink-900">{cr.proposedVersion.title}</h1>
                    {canRevise ? (
                      <Button
                        icon={<Pencil className="size-4" />}
                        onClick={() => {
                          setDraftContent(normalizeContent(cr.proposedVersion!.content));
                          setEditing(true);
                        }}
                      >
                        Edit proposal
                      </Button>
                    ) : null}
                  </div>
                  <DocumentReadView content={cr.proposedVersion.content} />
                </>
              )}
            </Card>
          ) : null}

          {tab === 'discussion' ? <Discussion cr={cr} onPosted={invalidate} canComment={can('changeRequest:comment')} /> : null}
        </div>

        <aside className="space-y-4">
          <Card>
            <CardHeader title="Approval status" />
            <div className="space-y-3 px-5 py-4">
              <div className="flex items-baseline justify-between">
                <span className="text-[13px] text-ink-600">Approvals</span>
                <span className={`text-lg font-semibold ${cr.approvals >= cr.requiredApprovals ? 'text-emerald-600' : 'text-ink-800'}`}>
                  {cr.approvals}
                  <span className="text-[13px] font-normal text-ink-400">/{cr.requiredApprovals}</span>
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-ink-100">
                <div
                  className={`h-full rounded-full transition-all ${cr.approvals >= cr.requiredApprovals ? 'bg-emerald-500' : 'bg-brand-500'}`}
                  style={{ width: `${Math.min(100, (cr.approvals / Math.max(1, cr.requiredApprovals)) * 100)}%` }}
                />
              </div>

              {cr.mergeBlockers.length ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <p className="flex items-center gap-1.5 text-[12px] font-semibold text-amber-800">
                    <ShieldAlert className="size-3.5" /> Not ready to merge
                  </p>
                  <ul className="mt-1 space-y-0.5 text-[12px] text-amber-900">
                    {cr.mergeBlockers.map((blocker) => (
                      <li key={blocker}>· {blocker}</li>
                    ))}
                  </ul>
                </div>
              ) : isOpen ? (
                <div className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] font-medium text-emerald-800">
                  <CheckCircle2 className="size-3.5" /> Ready to merge
                </div>
              ) : null}

              <p className="text-[11.5px] leading-relaxed text-ink-400">
                Policy: {cr.policy.minApprovals} approval{cr.policy.minApprovals === 1 ? '' : 's'} from {cr.policy.requiredRoles.join(' or ')}
                {cr.policy.allowSelfApproval ? '' : '; the author cannot approve their own request'}.
              </p>
            </div>
          </Card>

          {isOpen && can('changeRequest:review') && !isAuthor ? (
            <ReviewBox onSubmit={(decision, comment) => review.mutate({ decision, comment })} pending={review.isPending} current={myReview?.decision} />
          ) : null}

          {cr.reviewers.length ? (
            <Card>
              <CardHeader title="Reviewers" />
              <ul className="divide-y divide-ink-100">
                {cr.reviewers.map((reviewer) => {
                  const decision = cr.reviews.filter((r) => r.reviewer?.id === reviewer.id && r.current).at(-1);
                  return (
                    <li key={reviewer.id} className="flex items-center gap-2.5 px-5 py-2.5">
                      <Avatar name={reviewer.name} email={reviewer.email} size={24} />
                      <span className="min-w-0 flex-1 truncate text-[13px] text-ink-700">{reviewer.name}</span>
                      {decision ? (
                        <Badge tone={decision.decision === 'approve' ? 'success' : decision.decision === 'request_changes' ? 'warning' : 'neutral'}>
                          {decision.decision === 'approve' ? 'approved' : decision.decision === 'request_changes' ? 'changes' : 'commented'}
                        </Badge>
                      ) : (
                        <Badge tone="neutral">pending</Badge>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Card>
          ) : null}

          {cr.reviews.length ? (
            <Card>
              <CardHeader title="Review record" description="The audit trail for this decision." />
              <ul className="divide-y divide-ink-100">
                {cr.reviews.map((entry) => (
                  <li key={entry.id} className={`px-5 py-2.5 ${entry.current ? '' : 'opacity-50'}`}>
                    <div className="flex items-center gap-2">
                      <Badge tone={entry.decision === 'approve' ? 'success' : entry.decision === 'request_changes' ? 'warning' : 'neutral'}>
                        {entry.decision.replace('_', ' ')}
                      </Badge>
                      <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink-700">{entry.reviewer?.name ?? 'Unknown'}</span>
                    </div>
                    <p className="mt-0.5 text-[11.5px] text-ink-400">
                      {fmtDateTime(entry.createdAt)}
                      {entry.current ? '' : ' · superseded by a later revision'}
                    </p>
                    {entry.comment ? <p className="mt-1 text-[12.5px] text-ink-600">{entry.comment}</p> : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </aside>
      </div>

      <MergeModal open={mergeOpen} onClose={() => setMergeOpen(false)} cr={cr} onDone={invalidate} />
      <ConfirmDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={() => transition.mutate('rejected')}
        title="Decline this change request?"
        message="The proposal will be closed and the document left unchanged. The author can reopen it later."
        confirmLabel="Decline"
        loading={transition.isPending}
      />
    </>
  );
}

function ReviewBox({
  onSubmit,
  pending,
  current,
}: {
  onSubmit: (decision: ReviewDecision, comment?: string) => void;
  pending: boolean;
  current?: ReviewDecision;
}) {
  const [comment, setComment] = useState('');
  return (
    <Card>
      <CardHeader title="Your review" description={current ? `You last recorded: ${current.replace('_', ' ')}` : undefined} />
      <div className="space-y-3 px-5 py-4">
        <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment (optional)" />
        <div className="grid gap-2">
          <Button
            variant="success"
            icon={<Check className="size-4" />}
            loading={pending}
            onClick={() => {
              onSubmit('approve', comment || undefined);
              setComment('');
            }}
          >
            Approve
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              icon={<X className="size-4" />}
              onClick={() => {
                onSubmit('request_changes', comment || undefined);
                setComment('');
              }}
            >
              Request changes
            </Button>
            <Button
              icon={<MessageSquare className="size-4" />}
              onClick={() => {
                onSubmit('comment', comment || undefined);
                setComment('');
              }}
            >
              Comment only
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function Discussion({ cr, onPosted, canComment }: { cr: ChangeRequestDetailDto; onPosted: () => void; canComment: boolean }) {
  const { push } = useToast();
  const { session, can } = useSession();
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');

  const post = useMutation({
    mutationFn: (input: { body: string; parentId?: string | null }) => api.post(`/change-requests/${cr.id}/comments`, input),
    onSuccess: () => {
      setBody('');
      setReplyBody('');
      setReplyTo(null);
      onPosted();
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not post your comment', 'danger'),
  });

  const resolve = useMutation({
    mutationFn: (input: { id: string; resolved: boolean }) => api.patch(`/comments/${input.id}`, { resolved: input.resolved }),
    onSuccess: onPosted,
  });

  const remove = useMutation({
    mutationFn: (commentId: string) => api.del(`/comments/${commentId}`),
    onSuccess: onPosted,
  });

  const roots = cr.comments.filter((c) => !c.parentId);
  const repliesOf = (parentId: string) => cr.comments.filter((c) => c.parentId === parentId);

  const renderComment = (comment: CommentDto, nested = false) => (
    <div key={comment.id} className={nested ? 'mt-3 ml-8' : ''}>
      <div className={`rounded-lg border px-4 py-3 ${comment.resolvedAt ? 'border-emerald-200 bg-emerald-50/40' : 'border-ink-200 bg-white'}`}>
        <div className="flex items-center gap-2">
          {comment.author ? <Avatar name={comment.author.name} email={comment.author.email} size={22} /> : null}
          <span className="text-[13px] font-medium text-ink-800">{comment.author?.name ?? 'Unknown'}</span>
          <span className="text-[11.5px] text-ink-400">{fmtRelative(comment.createdAt)}</span>
          {comment.updatedAt ? <span className="text-[11.5px] text-ink-400">(edited)</span> : null}
          {comment.resolvedAt ? (
            <Badge tone="success" className="ml-auto">
              resolved
            </Badge>
          ) : null}
        </div>

        {comment.anchor?.sectionAnchor ? (
          <p className="mt-1.5 border-l-2 border-brand-300 pl-2 text-[11.5px] text-ink-500">
            on §{comment.anchor.sectionAnchor}
            {comment.anchor.quote ? `: “${comment.anchor.quote}”` : ''}
          </p>
        ) : null}

        <p className="mt-2 text-[13.5px] leading-relaxed whitespace-pre-wrap text-ink-700">{comment.body}</p>

        {canComment ? (
          <div className="mt-2 flex gap-3 text-[12px]">
            {!nested ? (
              <button type="button" onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)} className="text-ink-500 hover:text-brand-600">
                Reply
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => resolve.mutate({ id: comment.id, resolved: !comment.resolvedAt })}
              className="text-ink-500 hover:text-emerald-600"
            >
              {comment.resolvedAt ? 'Reopen' : 'Resolve'}
            </button>
            {comment.author?.id === session?.user.id || can('changeRequest:merge') ? (
              <button type="button" onClick={() => remove.mutate(comment.id)} className="text-ink-500 hover:text-rose-600">
                Delete
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {repliesOf(comment.id).map((reply) => renderComment(reply, true))}

      {replyTo === comment.id ? (
        <div className="mt-2 ml-8 flex gap-2">
          <CornerDownRight className="mt-2.5 size-4 shrink-0 text-ink-300" />
          <div className="flex-1">
            <Textarea rows={2} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="Write a reply…" autoFocus />
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="primary" disabled={!replyBody.trim()} loading={post.isPending} onClick={() => post.mutate({ body: replyBody, parentId: comment.id })}>
                Reply
              </Button>
              <Button size="sm" onClick={() => setReplyTo(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <Card className="px-5 py-4">
      {roots.length === 0 ? (
        <EmptyState icon={<MessageSquare className="size-8" />} title="No discussion yet" description="Ask a question or record your reasoning here." />
      ) : (
        <div className="space-y-3">{roots.map((comment) => renderComment(comment))}</div>
      )}

      {canComment ? (
        <div className="mt-5 border-t border-ink-100 pt-4">
          <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add to the discussion…" />
          <div className="mt-2 flex justify-end">
            <Button variant="primary" disabled={!body.trim()} loading={post.isPending} onClick={() => post.mutate({ body })}>
              Post comment
            </Button>
          </div>
        </div>
      ) : (
        <Alert tone="info">Your role can read the discussion but not take part in it.</Alert>
      )}
    </Card>
  );
}

function MergeModal({ open, onClose, cr, onDone }: { open: boolean; onClose: () => void; cr: ChangeRequestDetailDto; onDone: () => void }) {
  const { push } = useToast();
  const [bump, setBump] = useState<'major' | 'minor'>('minor');
  const [publish, setPublish] = useState(true);
  const [summary, setSummary] = useState(cr.title);
  const [effectiveFrom, setEffectiveFrom] = useState('');

  const merge = useMutation({
    mutationFn: () =>
      api.post(`/change-requests/${cr.id}/merge`, {
        versionBump: bump,
        publish,
        changeSummary: summary || undefined,
        effectiveFrom: effectiveFrom || null,
      }),
    onSuccess: () => {
      onDone();
      onClose();
      push(publish ? 'Change merged and published' : 'Change merged into the draft', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not merge', 'danger'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Accept and merge"
      description="The proposal becomes the document's content, and the change request is closed."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="success" loading={merge.isPending} onClick={() => merge.mutate()}>
            {publish ? 'Merge and publish' : 'Merge as draft'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Checkbox
          label="Publish immediately"
          hint="Leave unticked to land the change as an unpublished draft."
          checked={publish}
          onChange={(e) => setPublish(e.target.checked)}
        />

        {publish ? (
          <Field label="Version increment">
            <Select value={bump} onChange={(e) => setBump(e.target.value as 'major' | 'minor')}>
              <option value="minor">Minor — clarification or correction</option>
              <option value="major">Major — substantive change of obligation</option>
            </Select>
          </Field>
        ) : null}

        <Field label="Summary of change" hint="Recorded in the version history and the PDF change record.">
          <Input value={summary} onChange={(e) => setSummary(e.target.value)} />
        </Field>

        {publish ? (
          <Field label="Effective from" hint="Defaults to today.">
            <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </Field>
        ) : null}
      </div>
    </Modal>
  );
}
